import { AskRequest } from '@hone-automation/common';

/**
 * Parse a YYYY-MM-DD date string as local date (not UTC)
 * This prevents timezone issues where "2024-01-15" parsed as UTC
 * shows as Jan 14 in timezones ahead of UTC (e.g., Europe)
 */
export const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Available date fields for grouping/sorting */
export type DateField = 'lastMessage' | 'dueDate' | 'createdAt' | 'transactionDate';

/** Labels for date field options */
export const dateFieldLabels: Record<DateField, string> = {
  lastMessage: 'Last Message',
  dueDate: 'Due Date',
  createdAt: 'Creation Date',
  transactionDate: 'Transaction Date',
};

export type DateGroupKey =
  | 'blockers' // Special group for blockers at top of Me/Team/Client views
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'noDate' // Requests without the selected date field
  | `month-${string}` // e.g., "month-2025-08"
  | `year-${string}`; // e.g., "year-2024"

export interface DateGroup {
  key: DateGroupKey;
  label: string;
  requests: AskRequest[];
  sortOrder: number; // Lower = older (for oldest-first sorting)
}

/**
 * Get the date of the last comment, or fallback to updatedAt/createdAt
 */
export const getLastActivityDate = (request: AskRequest): Date => {
  if (request.comments && request.comments.length > 0) {
    const lastComment = request.comments[request.comments.length - 1];
    // @ts-ignore - createdAt exists on comment
    const commentDate = lastComment?.createdAt;
    if (commentDate) {
      return new Date(commentDate);
    }
  }
  // Fallback to request dates
  const dateValue = request.updatedAt || request.createdAt;
  return dateValue ? new Date(dateValue) : new Date();
};

/**
 * Get the date for a request based on the selected date field
 * Returns null if the field doesn't exist on the request
 */
export const getDateForField = (request: AskRequest, field: DateField): Date | null => {
  switch (field) {
    case 'lastMessage':
      return getLastActivityDate(request);

    case 'dueDate':
      // @ts-ignore - dueDate may exist on request
      if (request.dueDate) {
        return parseLocalDate(request.dueDate);
      }
      return null;

    case 'createdAt':
      if (request.createdAt) {
        return new Date(request.createdAt);
      }
      return null;

    case 'transactionDate': {
      // @ts-ignore - transaction date field (check actual field name)
      const txnDate = request.transactionDate || request.transaction?.date;
      if (txnDate) {
        return new Date(txnDate);
      }
      return null;
    }

    default:
      return getLastActivityDate(request);
  }
};

/**
 * Check if a request has a transaction date (for conditional visibility)
 */
export const hasTransactionDate = (request: AskRequest): boolean => {
  // @ts-ignore - check for transaction-related types
  const type = request.type?.toLowerCase() || '';
  return type.includes('customer') || type.includes('accountant');
};

/**
 * Determine which date group a request belongs to
 */
export const getDateGroupKey = (date: Date, now: Date): DateGroupKey => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Get start of current week (Monday)
  const currentWeekStart = new Date(today);
  const dayOfWeek = today.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  currentWeekStart.setDate(currentWeekStart.getDate() - daysToMonday);

  // Get start of last week (previous Monday)
  const lastWeekStart = new Date(currentWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(currentWeekStart);
  lastWeekEnd.setDate(lastWeekEnd.getDate() - 1); // Sunday before current week

  // Get start of current month
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  // Get start of last month
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(currentMonthStart);
  lastMonthEnd.setDate(lastMonthEnd.getDate() - 1);

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Today
  if (dateOnly.getTime() === today.getTime()) {
    return 'today';
  }

  // Yesterday
  if (dateOnly.getTime() === yesterday.getTime()) {
    return 'yesterday';
  }

  // This Week (current calendar week, excluding today/yesterday)
  if (dateOnly >= currentWeekStart && dateOnly < today) {
    return 'thisWeek';
  }

  // Last Week (previous calendar week, Mon-Sun, excluding yesterday if it falls there)
  if (dateOnly >= lastWeekStart && dateOnly <= lastWeekEnd) {
    return 'lastWeek';
  }

  // This Month (current calendar month, excluding this week/today/yesterday)
  if (dateOnly >= currentMonthStart && dateOnly < today) {
    return 'thisMonth';
  }

  // Last Month (previous calendar month)
  if (dateOnly >= lastMonthStart && dateOnly <= lastMonthEnd) {
    return 'lastMonth';
  }

  // Previous months in current year (before last month)
  if (date.getFullYear() === now.getFullYear() && date.getMonth() < now.getMonth() - 1) {
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return `month-${monthKey}`;
  }

  // Previous years
  if (date.getFullYear() < now.getFullYear()) {
    // If it's just last year, group by month
    if (date.getFullYear() === now.getFullYear() - 1) {
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return `month-${monthKey}`;
    }
    // Older years: group by year
    return `year-${date.getFullYear()}`;
  }

  // Fallback: current period (this week, this month but not today/yesterday)
  // Group with "This Week" or just show as recent
  return 'thisMonth';
};

/**
 * Get human-readable label for a date group
 */
export const getGroupLabel = (key: DateGroupKey, now: Date): string => {
  if (key === 'today') return 'Today';
  if (key === 'yesterday') return 'Yesterday';
  if (key === 'thisWeek') return 'This Week';
  if (key === 'lastWeek') return 'Last Week';
  if (key === 'thisMonth') return 'This Month';
  if (key === 'lastMonth') return 'Last Month';

  if (key.startsWith('month-')) {
    const [, yearMonth] = key.split('month-');
    const [year, month] = yearMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    // Format as "August 2024" or "March 2025"
    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
    return `${monthName} ${year}`;
  }

  if (key.startsWith('year-')) {
    const year = key.split('year-')[1];
    return year;
  }

  return key;
};

/**
 * Get sort order for a group (lower = older, for oldest-first default)
 */
export const getGroupSortOrder = (key: DateGroupKey, now: Date): number => {
  if (key === 'today') return 999999;
  if (key === 'yesterday') return 999998;
  if (key === 'thisWeek') return 999997;
  if (key === 'lastWeek') return 999996;
  if (key === 'thisMonth') return 999995;
  if (key === 'lastMonth') return 999994;

  if (key.startsWith('month-')) {
    const [, yearMonth] = key.split('month-');
    const [year, month] = yearMonth.split('-').map(Number);
    // Convert to sortable number: 202508 for Aug 2025
    return year * 100 + month;
  }

  if (key.startsWith('year-')) {
    const year = parseInt(key.split('year-')[1], 10);
    return year;
  }

  return 0;
};

/**
 * Group requests by date and sort groups
 */
export const groupRequestsByDate = (
  requests: AskRequest[],
  sortAscending: boolean = true, // true = oldest first
  dateField: DateField = 'lastMessage'
): DateGroup[] => {
  const now = new Date();
  const groupMap = new Map<DateGroupKey, AskRequest[]>();
  const noDateGroup: AskRequest[] = []; // Requests without the selected date field

  // Assign each request to a group
  for (const request of requests) {
    const fieldDate = getDateForField(request, dateField);

    if (fieldDate === null) {
      // No date for this field - collect separately
      noDateGroup.push(request);
      continue;
    }

    const groupKey = getDateGroupKey(fieldDate, now);

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }
    groupMap.get(groupKey)!.push(request);
  }

  // Convert to array and add metadata
  const groups: DateGroup[] = Array.from(groupMap.entries()).map(([key, reqs]) => ({
    key,
    label: getGroupLabel(key, now),
    requests: reqs,
    sortOrder: getGroupSortOrder(key, now),
  }));

  // Sort groups: oldest first (ascending sortOrder) or newest first (descending)
  groups.sort((a, b) => (sortAscending ? a.sortOrder - b.sortOrder : b.sortOrder - a.sortOrder));

  // Sort requests within each group by the selected date field
  for (const group of groups) {
    group.requests.sort((a, b) => {
      const dateA = getDateForField(a, dateField)?.getTime() || 0;
      const dateB = getDateForField(b, dateField)?.getTime() || 0;
      return sortAscending ? dateA - dateB : dateB - dateA;
    });
  }

  // Add "No Date" group if there are requests without the selected date field
  // Always put at the END of the list, regardless of sort direction
  if (noDateGroup.length > 0) {
    const noDateKey = 'noDate' as DateGroupKey;
    groups.push({
      key: noDateKey,
      label: `No ${dateFieldLabels[dateField]}`,
      requests: noDateGroup,
      sortOrder: sortAscending ? 1000000 : -1, // Always at END (after newest or after oldest)
    });
    // Re-sort to include the no-date group
    groups.sort((a, b) => (sortAscending ? a.sortOrder - b.sortOrder : b.sortOrder - a.sortOrder));
  }

  return groups;
};
